/**
 * Default Workflow Configuration Component
 *
 * Fallback component for workflows without specific configuration tags
 * Allows execution of workflows without additional configuration
 */

import React from 'react';
import { Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { WorkflowConfigProps } from '../../types/workflow-config';

export const DefaultConfig: React.FC<WorkflowConfigProps> = ({
  workflow,
  onExecute,
  onClose,
  isLoading = false,
}) => {
  const handleExecute = async () => {
    await onExecute({});
  };

  return (
    <div className="space-y-6">
      {/* Info Message */}
      <div className="rounded-lg bg-primary/5 border border-primary/20 p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <Info className="h-5 w-5 text-primary" />
          </div>
          <div className="ml-3 flex-1">
            <p className="text-sm text-muted-foreground">
              This workflow has no special configuration options. You can execute it directly with default settings.
            </p>
          </div>
        </div>
      </div>

      {/* Workflow Info */}
      <div className="border-t border-border pt-4">
        <dl className="space-y-2">
          <div className="flex justify-between">
            <dt className="text-sm font-medium text-muted-foreground">Status</dt>
            <dd className="text-sm">
              <Badge variant={workflow.active ? 'default' : 'secondary'}>
                {workflow.active ? 'Active' : 'Inactive'}
              </Badge>
            </dd>
          </div>
          {workflow.tags && workflow.tags.length > 0 && (
            <div className="flex justify-between">
              <dt className="text-sm font-medium text-muted-foreground">Tags</dt>
              <dd className="text-sm text-foreground">
                {workflow.tags.map((tag) => tag.name).join(', ')}
              </dd>
            </div>
          )}
        </dl>
      </div>

      {/* Actions */}
      <div className="flex justify-end space-x-3 pt-4">
        <Button
          variant="ghost"
          onClick={onClose}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          onClick={handleExecute}
          disabled={isLoading}
        >
          {isLoading ? 'Executing...' : 'Execute Workflow'}
        </Button>
      </div>
    </div>
  );
};
